import { from } from '@/helpers/range'
import { last, previousOrFirst } from '@/helpers/array'
import { linear, quadratic, cubic } from '@/helpers/interpolation'
import { InterpolationMode } from './constants'

/**
 * Returns if a value is interpolable (i.e. if it is a number)
 * @param {*} value Value
 * @returns {Boolean}
 */
export function isInterpolable (value) {
  return typeof value === 'number'
}

/**
 * Returns if a value is not interpolable (i.e. if it is not a number).
 * @param {*} value Value
 * @returns {Boolean}
 */
export function isNotInterpolable (value) {
  return !isInterpolable(value)
}

export function create (keys = [], defaultValue = 0) {
  return {
    keys,
    defaultValue
  }
}

export function createKey (value, control = [], interpolationMode = InterpolationMode.LINEAR) {
  return {
    value,
    control,
    interpolationMode
  }
}

/**
 * Sets a
 * @param {*} param0
 * @param {*} time
 * @param {*} param2
 */
export function setValueAt ({ keys }, time, { value, control, interpolationMode }) {
  const index = keys.findIndex(([currentKeyTime]) => time < currentKeyTime)
  // If control values are not passed in then we calculate
  // which control values are appropriate based on
  // surrounding values.
  if (!control && index < -1 && keys.length > 0) {
    control = [last(keys).value, value]
  } else if (!control && index < -1 && keys.length === 0) {
    control = [value, value]
  } else if (!control && index > 0) {
    control = [
      previousOrFirst(keys, index).value,
      keys[index].value
    ]
  }
  // Insert the key in the specified index.
  keys.splice(index, 0, {
    value,
    control,
    interpolationMode
  })
}

/**
 * Returns the current value of an animated value
 * @param {AnimatedValue} animatedValue Animated value
 * @param {number} time Time
 * @param {number} startIndex Index where the search will start
 * @returns {*}
 */
export function getValueAt ({ keys, defaultValue }, time, startIndex = 0) {
  for (let index = startIndex; index < keys.length; index++) {
    const [currentKeyTime, currentKey] = keys[index]
    // If we are at the initial key and before it, we
    // return it's value.
    // TODO: Maybe we could "extrapolate" using the
    // interpolation functions.
    if (index === 0 && currentKeyTime > time) {
      return currentKey.value
    }

    // If we are at the final key or the value cannot be
    // interpolated, then we return the current key value.
    if (index === keys.length - 1) {
      return currentKey.value
    }

    // Retrieve the next key to get the interpolated value
    // between the current key and the next key.
    const [nextKeyTime, nextKey] = keys[index + 1]
    if (!(currentKeyTime <= time && nextKeyTime > time)) {
      continue
    }

    // Return currentKey value if this value is not interpolable.
    if (isNotInterpolable(currentKey.value)) {
      return currentKey.value
    }

    // start, controls and end values used for interpolation.
    const {
      interpolationMode,
      value: valueStart,
      control: [
        valueControlA,
        valueControlB
      ]
    } = currentKey
    const valueEnd = nextKey.value
    // calculate interpolation point in time.
    const x = from(time, currentKeyTime, nextKeyTime)
    // interpolate accordingly to interpolationMode.
    switch (interpolationMode) {
      default:
        throw new Error(`Invalid interpolation mode ${interpolationMode}`)
      case InterpolationMode.LINEAR:
        return linear(x, valueStart, valueEnd)
      case InterpolationMode.QUADRATIC:
        return quadratic(x, valueStart, valueControlA, valueEnd)
      case InterpolationMode.CUBIC:
        return cubic(x, valueStart, valueControlA, valueControlB, valueEnd)
    }
  }

  // If keys are empty, then we return the defaultValue.
  return defaultValue
}

export default {
  isInterpolable,
  isNotInterpolable,
  create,
  createKey,
  setValueAt,
  getValueAt
}
